import type { ConceptAssignment } from '@/db/Types'
import type { ConceptSide, TargetingMode } from '@/types/Concept.types'
import type { UseConceptAIParserReturn } from '@/hooks/UseConceptAIParser'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { useDelayedUnmount } from '@/hooks'
import './concept-details-panel.css'

const ANIMATION_DURATION = 800

const TARGETING_OPTIONS = [
	{ value: 'absolute_role', label: 'By Player' },
	{ value: 'relative_selector', label: 'By Alignment (#1, #2, #3)' },
] as const

const NUMBERED_SELECTOR_LABELS: Record<string, string> = {
	'#1_receiver': '#1 (Outermost)',
	'#2_receiver': '#2',
	'#3_receiver': '#3',
	'#4_receiver': '#4',
	'#5_receiver': '#5 (Innermost)',
}

interface ConceptDetailsPanelProps {
	showPanel: boolean
	mode: 'visual' | 'ai'
	targetingMode: TargetingMode
	onTargetingModeChange: (mode: TargetingMode) => void
	defaultSide: ConceptSide | null
	onDefaultSideChange: (side: ConceptSide | null) => void
	assignments: ConceptAssignment[]
	onRemoveAssignment: (index: number) => void
	code: string
	onCodeChange: (code: string) => void
	aiParser: UseConceptAIParserReturn
	onAcceptAIParse: () => void
}

export function ConceptDetailsPanel({
	showPanel,
	mode,
	targetingMode,
	onTargetingModeChange,
	defaultSide,
	onDefaultSideChange,
	assignments,
	onRemoveAssignment,
	code,
	onCodeChange,
	aiParser,
	onAcceptAIParse,
}: ConceptDetailsPanelProps) {
	const shouldRenderContent = useDelayedUnmount(showPanel, ANIMATION_DURATION)

	return (
		<div
			className="concept-details-panel"
			style={{ width: showPanel ? '280px' : '0px' }}
		>
			{shouldRenderContent && (
				<div className="concept-details-panel__content">
					<div className="concept-details-panel__scroll">
						{mode === 'visual' ? (
							<>
								<h3 className="concept-details-panel__title">Assignments</h3>

								<div className="concept-details-panel__targeting">
									<label className="concept-details-panel__label">Targeting Mode</label>
									<div className="concept-details-panel__toggle">
										{TARGETING_OPTIONS.map(opt => (
											<button
												key={opt.value}
												className="concept-details-panel__toggle-btn"
												data-active={targetingMode === opt.value || undefined}
												onClick={() => onTargetingModeChange(opt.value)}
												type="button"
											>
												{opt.label}
											</button>
										))}
									</div>
								</div>

								{targetingMode === 'relative_selector' && (
									<div className="concept-details-panel__side-preference">
										<label className="concept-details-panel__label">Side Preference</label>
										<Select
											value={defaultSide ?? 'any'}
											onValueChange={(v) => onDefaultSideChange(v === 'any' ? null : v as ConceptSide)}
										>
											<SelectTrigger className="concept-details-panel__side-select">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="any">Any Side</SelectItem>
												<SelectItem value="strong">Strong Side</SelectItem>
												<SelectItem value="weak">Weak Side</SelectItem>
												<SelectItem value="left">Left</SelectItem>
												<SelectItem value="right">Right</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}

								{assignments.length === 0 ? (
									<div className="concept-details-panel__empty">
										<p>No assignments yet</p>
										<p className="concept-details-panel__empty-hint">Tap a player on the canvas to assign a route</p>
									</div>
								) : (
									<div className="concept-details-panel__assignment-list">
										{assignments.map((assignment, i) => {
											const selectorLabel = assignment.selector_type
												? NUMBERED_SELECTOR_LABELS[assignment.selector_type] ?? assignment.selector_type
												: null

											return (
												<div key={assignment.id} className="concept-details-panel__assignment-card">
													<span className="concept-details-panel__assignment-role">
														{targetingMode === 'absolute_role'
															? (assignment.role ?? `Player ${i + 1}`)
															: (selectorLabel ?? `#${i + 1}`)}
													</span>
													<span className="concept-details-panel__assignment-type">
														{assignment.drawing_data ? 'Route assigned' : 'Unassigned'}
													</span>
													<button
														className="concept-details-panel__assignment-remove"
														onClick={() => onRemoveAssignment(i)}
														type="button"
														aria-label="Remove assignment"
													>
														&#x2715;
													</button>
												</div>
											)
										})}
									</div>
								)}

								<div className="concept-details-panel__code-field">
									<label className="concept-details-panel__label">Vocabulary Code</label>
									<Input
										placeholder="Auto-generated if empty..."
										value={code}
										onChange={(e) => onCodeChange(e.target.value)}
									/>
								</div>
							</>
						) : (
							<div className="concept-details-panel__ai-section">
								<div className="concept-details-panel__ai-input">
									<label className="concept-details-panel__ai-label">
										Describe your concept in plain English:
									</label>
									<textarea
										className="concept-details-panel__ai-textarea"
										placeholder='e.g., "Orlando is the 1 running a go and the 2 running an out"'
										value={aiParser.description}
										onChange={(e) => aiParser.setDescription(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && !e.shiftKey) {
												e.preventDefault()
												aiParser.parse()
											}
										}}
										rows={3}
									/>
									<div className="concept-details-panel__ai-actions">
										<Button
											onClick={aiParser.parse}
											disabled={aiParser.isParsing || aiParser.description.trim().length < 5}
										>
											{aiParser.isParsing ? 'Parsing...' : 'Parse'}
										</Button>
									</div>
								</div>

								{aiParser.error && (
									<div className="concept-details-panel__ai-error">
										<p>{aiParser.error}</p>
										<p className="concept-details-panel__ai-tips">
											Tips: Name each player's assignment. Use player names (X, Y, Z) or numbers (#1, #2, #3).
											Specify route types: go, slant, out, in, curl, post, corner, flat.
										</p>
									</div>
								)}

								{aiParser.parsedConcept && (
									<div className="concept-details-panel__ai-preview">
										<h3 className="concept-details-panel__ai-preview-title">Parsed Result</h3>

										<div className="concept-details-panel__ai-preview-meta">
											<span className="concept-details-panel__ai-preview-name">{aiParser.parsedConcept.name}</span>
											<span className="concept-details-panel__ai-preview-category">{aiParser.parsedConcept.category}</span>
											<span className="concept-details-panel__ai-preview-targeting">
												{aiParser.parsedConcept.targetingMode === 'absolute_role' ? 'By Player' : 'By Alignment'}
											</span>
										</div>

										{aiParser.parsedConcept.confidence < 0.8 && (
											<div className="concept-details-panel__ai-low-confidence">
												Low confidence — review carefully before saving
											</div>
										)}

										{aiParser.parsedConcept.ambiguities.length > 0 && (
											<div className="concept-details-panel__ai-ambiguities">
												<p>Not sure about:</p>
												<ul>
													{aiParser.parsedConcept.ambiguities.map((a, i) => (
														<li key={i}>{a}</li>
													))}
												</ul>
											</div>
										)}

										<div className="concept-details-panel__ai-assignments">
											<h4>Assignments</h4>
											{aiParser.parsedConcept.assignments.map((a, i) => (
												<div key={i} className="concept-details-panel__ai-assignment">
													<span className="concept-details-panel__ai-assignment-target">
														{NUMBERED_SELECTOR_LABELS[a.target] ?? a.target}
													</span>
													<span className="concept-details-panel__ai-assignment-arrow">&rarr;</span>
													<span className="concept-details-panel__ai-assignment-route">
														{a.routeType}{a.routeDetails ? ` (${a.routeDetails})` : ''}
													</span>
												</div>
											))}
										</div>

										<div className="concept-details-panel__ai-preview-actions">
											<Button onClick={onAcceptAIParse}>
												Save
											</Button>
											<Button variant="outline" onClick={onAcceptAIParse}>
												Edit manually
											</Button>
											<Button variant="ghost" onClick={aiParser.clear}>
												Try again
											</Button>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
